import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { voiceApi, errorMessage } from './voiceApi';
import { formatPhone } from './callFormat';

const NUMBERS = [
    { field: 'phone', label: 'Student', contact_label: 'student' },
    { field: 'parent_phone', label: 'Parent', contact_label: 'parent' },
    { field: 'parent_phone_2', label: 'Parent 2', contact_label: 'parent_2' },
];

/**
 * Search students by name or consultation number.
 *   mode="call"   → one button per number on file; onPick({ student, number, contact_label })
 *   mode="attach" → one "Attach" button per student;   onPick({ student })
 */
const StudentSearch = ({ mode = 'call', onPick, autoFocus = false, placeholder, pickLabel = 'Attach' }) => {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const timer = useRef(null);
    const seq = useRef(0);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        const term = q.trim();
        if (term.length < 2) { setResults([]); setLoading(false); return undefined; }
        setLoading(true);
        const mySeq = ++seq.current;
        timer.current = setTimeout(async () => {
            try {
                const { students } = await voiceApi.searchStudents(term, 8);
                if (mySeq === seq.current) { setResults(students); setError(null); }
            } catch (err) {
                if (mySeq === seq.current) setError(errorMessage(err));
            } finally {
                if (mySeq === seq.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer.current);
    }, [q]);

    return (
        <Box>
            <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={placeholder || 'Student name or consultation #'}
                size="small"
                fullWidth
                autoFocus={autoFocus}
                InputProps={{ endAdornment: loading ? <CircularProgress size={14} /> : null }}
            />
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {results.length > 0 && (
                <Box sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto' }}>
                    {results.map((s) => {
                        const numbers = NUMBERS.filter((n) => s[n.field]);
                        return (
                            <Box key={s.uuid} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {s.first_name} {s.last_name}
                                    {s.korean_name ? <Typography component="span" variant="caption" color="text.secondary"> {s.korean_name}</Typography> : null}
                                    {s.consultation_number ? <Typography component="span" variant="caption" color="text.secondary"> · #{s.consultation_number}</Typography> : null}
                                    {s.is_potential_student ? <Typography component="span" variant="caption" color="warning.main"> · potential</Typography> : null}
                                </Typography>
                                {mode === 'attach' ? (
                                    <Button size="small" onClick={() => onPick({ student: s })}>{pickLabel}</Button>
                                ) : numbers.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">No phone numbers on file</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {numbers.map((n) => (
                                            <Button
                                                key={n.field}
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => onPick({ student: s, number: s[n.field], contact_label: n.contact_label })}
                                                sx={{ textTransform: 'none', py: 0 }}
                                            >
                                                {n.label} {formatPhone(s[n.field])}
                                            </Button>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
            {!loading && q.trim().length >= 2 && results.length === 0 && !error && (
                <Typography variant="caption" color="text.secondary">No students found.</Typography>
            )}
        </Box>
    );
};

export default StudentSearch;
